import { ClassModel, Instance, action, register, types } from "@gadgetinc/mobx-quick-tree";
import {
  EditDarkProductionTheme,
  EditDarkTheme,
  EditLayout,
  EditLightProductionTheme,
  EditLightTheme,
} from "@gadgetinc/themes/src/EditTheme";
import { clamp } from "lodash";
import qs from "qs";
import { createContext, useContext } from "react";
import type { EnvironmentName } from "state-trees/src/EnvironmentNames";
import { ProductionEnvironmentName } from "state-trees/src/EnvironmentNames";
import { FlyoutKind } from "../../components/edit/workspace/Flyout/Flyout";
import { CurrentFlags } from "../../lib/flags";
import { QuerySnapshot } from "../log-viewer/loki";
import { persistInLocalStorage } from "./persistInLocalStorage";

export const storageKey = "interaction-prefs";

export const defaultStatusDrawerHeight = 242;
export const maxStatusDrawerHeight = 720;

@register
export class CodeEditorPreferences extends ClassModel({
  insertSpaces: types.optional(types.boolean, true),
  tabSize: types.optional(types.number, 2),
}) {}

@register
export class LogViewerPreferences extends ClassModel({
  lastQuery: types.optional(QuerySnapshot, {}),
}) {
  @action
  setLastQuery(query: Instance<typeof QuerySnapshot>) {
    this.lastQuery = query;
  }
}

export enum Banner {
  SHOPIFY_INSTALL_PROMPT = "SHOPIFY_INSTALL_PROMPT",
  SHOPIFY_CONNECTIONS_INTRO = "CONNECTIONS_INTRO",
  FILE_SYNC_INTRO = "FILE_SYNC_INTRO",
  SHOPIFY_PROTECTED_ACCESS = "SHOPIFY_PROTECTED_ACCESS",
  VITE_SYNC_FILES_LOCALLY_CARD = "VITE_SYNC_FILES_LOCALLY",
  JOIN_DISCORD_CARD = "JOIN_DISCORD",
  WEB_APP_ONBOARDING = "WEB_APP_ONBOARDING",
  HUBSPOT_CHAT_PROMPT = "HUBSPOT_CHAT_PROMPT",
}

export enum Language {
  JS = ".js",
  TS = ".ts",
}

@register
export class InteractionPreferences extends ClassModel({
  showHelp: types.optional(types.boolean, false),
  mouseEnabled: types.optional(types.boolean, true),
  behaviourPanelSize: types.optional(types.number, EditLayout.resizablePaneStartWidth),
  darkMode: types.optional(types.enumeration(["on", "auto", "off"]), "auto"),
  defaultLanguage: types.optional(types.frozen<Language>(), Language.JS),
  isStatusDrawerOpen: types.optional(types.boolean, false),
  isMobileWarningHidden: types.optional(types.boolean, false),
  statusDrawerHeight: types.optional(types.number, defaultStatusDrawerHeight),
  inProduction: types.optional(types.boolean, false),
  dismissedBanners: types.array(types.string),
  activeFlyout: types.maybe(types.frozen<FlyoutKind>()),

  codeEditor: types.optional(CodeEditorPreferences, {}),
  logViewer: types.optional(LogViewerPreferences, {}),
}) {
  @action
  afterCreate() {
    persistInLocalStorage(storageKey, this);
  }
  @action
  toggleHelp() {
    this.showHelp = !this.showHelp;
  }
  @action
  toggleMouseEnabled() {
    this.mouseEnabled = !this.mouseEnabled;
  }
  @action
  setBehaviourPanelSize(width: number) {
    this.behaviourPanelSize = clamp(width, 400, 800);
  }
  @action
  setDarkMode(mode: "on" | "auto" | "off") {
    this.darkMode = mode;
  }
  @action
  setDefaultLanguage(language: Language) {
    this.defaultLanguage = language;
  }
  @action
  setIsStatusDrawerOpen(isOpen: boolean) {
    this.isStatusDrawerOpen = isOpen;
  }
  @action
  toggleStatusDrawer() {
    this.isStatusDrawerOpen = !this.isStatusDrawerOpen;
  }
  @action
  dismissMobileWarning() {
    this.isMobileWarningHidden = true;
  }
  @action
  setStatusDrawerHeight(height: number) {
    const normalizedHeight = Math.min(maxStatusDrawerHeight, Math.max(defaultStatusDrawerHeight, height));
    this.statusDrawerHeight = normalizedHeight;
  }
  @action
  removeDismissedBanner(banner: Banner) {
    this.dismissedBanners.replace(this.dismissedBanners.filter((b) => (b as Banner) !== banner));
  }
  @action
  addDismissedBanner(banner: Banner) {
    if (!this.dismissedBanners.includes(banner)) {
      this.dismissedBanners.push(banner);
    }
  }
  @action
  setActiveFlyout(flyout: FlyoutKind) {
    this.activeFlyout = flyout;
  }
  @action
  clearActiveFlyout() {
    this.activeFlyout = undefined;
  }
  @action
  toggleInProduction() {
    this.inProduction = !this.inProduction;

    if (window) {
      const queryString = qs.parse(window.location.search.slice(1));

      if (this.inProduction) queryString.production = "1";
      else delete queryString.production;

      const newURL = window.location.origin + window.location.pathname + `?${qs.stringify(queryString)}`;
      window.location.href = newURL;
    }
  }
  get isJSDefaultLanguage() {
    return this.defaultLanguage === Language.JS;
  }
  get isTSDefaultLanguage() {
    return this.defaultLanguage === Language.TS;
  }
  get systemInDarkMode() {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }
  get inDarkMode() {
    return this.darkMode == "on" || (this.darkMode == "auto" && CurrentFlags.features.darkMode && this.systemInDarkMode);
  }

  get currentTheme() {
    if (this.inDarkMode) {
      return EditDarkTheme;
    } else {
      return EditLightTheme;
    }
  }
  get currentProductionTheme() {
    if (this.inDarkMode) {
      return EditDarkProductionTheme;
    } else {
      return EditLightProductionTheme;
    }
  }

  get eligibleForChatPopup() {
    if (typeof window == "undefined") return false;

    if (this.dismissedBanners.includes(Banner.HUBSPOT_CHAT_PROMPT)) return false;
    if (window?.GadgetEditConfig?.isWalkthroughApp) return false;
    const user = window?.GadgetEditConfig?.currentUser;

    if (user?.isStaff) return false;

    if (user?.createdDate) {
      const createdDate = new Date(user.createdDate);
      const now = new Date();

      // open the hubspot chat widget by default if the user is under 2 days old
      if (now.getTime() - createdDate.getTime() < 1000 * 60 * 60 * 24 * 2) {
        console.debug("[hubspot] showing hubspot immediately as the user is new");
        return true;
      }
    }

    return false;
  }

  currentEnvironmentTheme(environmentName?: EnvironmentName) {
    if (environmentName == ProductionEnvironmentName) {
      return this.currentProductionTheme;
    } else {
      return this.currentTheme;
    }
  }

  @action
  toggleDarkMode() {
    if (this.darkMode == "auto") {
      if (this.systemInDarkMode) {
        this.setDarkMode("off");
      } else {
        this.setDarkMode("on");
      }
    } else {
      this.setDarkMode(this.darkMode == "on" ? "off" : "on");
    }
  }
}

export const InteractionPreferencesContext = createContext<Instance<typeof InteractionPreferences>>(null as any);
export const useInteractionPreferences = () => useContext(InteractionPreferencesContext);
